import { Component, Input, OnInit } from '@angular/core';
import { APIManager, Service, ServiceService } from '@flashvpn-io/web-core';
import QRCode from 'qrcode'
import { Subscription } from 'rxjs';

import { MenuService } from '../menu/menu.service';
import { ToastService } from '../ui/toast/toast.service';

interface ClientConfig {
  name: string;
  description?: string;
  icon: string;
  guidePath: string;
  downloadLinks: string[];
  platform: string[];
  urlScheme?: string;
  proxyDownloadLink: boolean;
}

const AllPlatformClientsConfig: ClientConfig[] = [
  {
    name: 'Terraka',
    icon: 'assets/images/third-party/terraka.svg',
    guidePath: 'https://terraka.app/apps',
    platform: ['windows', 'macos', 'android', 'ios', 'ubuntu'],
    downloadLinks: ['https://terraka.app/apps'],
    urlScheme: 'terraka://',
    proxyDownloadLink: false,
  },
  {
    name: 'Quantumult X',
    icon: 'assets/images/third-party/QuantumultX.webp',
    guidePath: 'how-to-use-rubiz-with-quantumultx',
    platform: ['ios'],
    downloadLinks: ['https://apps.apple.com/us/app/quantumult-x/id1443988620'],
    urlScheme: 'quantumult-x://',
    proxyDownloadLink: false,
  },
  {
    name: 'Stash',
    icon: 'assets/images/third-party/Stash.webp',
    guidePath: 'how-to-use-rubiz-with-stash',
    platform: ['ios'],
    downloadLinks: ['https://apps.apple.com/us/app/stash/id1596063349'],
    urlScheme: 'stash://',
    proxyDownloadLink: false,
  },
  {
    name: 'Shadowrocket',
    icon: 'assets/images/third-party/Shadowrocket.webp',
    guidePath: 'how-to-use-rubiz-with-shadowrocket',
    platform: ['ios'],
    downloadLinks: ['https://apps.apple.com/us/app/shadowrocket/id932747118'],
    urlScheme: 'shadowrocket://',
    proxyDownloadLink: false,
  },
  {
    name: 'Clash For Apple Silicon',
    icon: 'assets/images/third-party/clash.svg',
    guidePath: 'how-to-use-rubiz-with-clash-verge',
    platform: ['macos'],
    downloadLinks: [],
    urlScheme: 'clash://',
    proxyDownloadLink: true,
  },
  {
    name: 'Clash For Intel Chip',
    icon: 'assets/images/third-party/clash.svg',
    guidePath: 'how-to-use-rubiz-with-clash-verge',
    platform: ['macos'],
    downloadLinks: [],
    urlScheme: 'clash://',
    proxyDownloadLink: true,
  },
  {
    name: 'Clash X32',
    icon: 'assets/images/third-party/clash.svg',
    guidePath: 'how-to-use-rubiz-with-clash-verge',
    platform: ['windows'],
    downloadLinks: [],
    urlScheme: 'clash://',
    proxyDownloadLink: true,
  },
  {
    name: 'Clash X64',
    icon: 'assets/images/third-party/clash.svg',
    guidePath: 'how-to-use-rubiz-with-clash-verge',
    platform: ['windows'],
    downloadLinks: [],
    urlScheme: 'clash://',
    proxyDownloadLink: true,
  },
  {
    name: 'ClashMeta For Android',
    icon: 'assets/images/third-party/clash-meta.png',
    guidePath: 'how-to-use-rubiz-with-clashmeta',
    platform: ['android'],
    downloadLinks: [],
    urlScheme: 'clash://',
    proxyDownloadLink: true,
  }
];

@Component({
  selector: 'app-third-party-client',
  templateUrl: './third-party-client.component.html',
  styleUrls: ['./third-party-client.component.css']
})
export class ThirdPartyClientComponent implements OnInit {
  @Input() deliveryIps: string[] = [];
  private platformSubscription: Subscription;
  private serviceSubscription: Subscription;
  private currentPlatform: string = 'windows';
  public currentPlatformClientsConfig: ClientConfig[] = [];
  public selectedClient: ClientConfig = AllPlatformClientsConfig[0];
  public service: Service | null = null;
  public subscriptionUrls: string[] = [];
  public subscribePath: string = '';
  public modalVisible = false;
  public qrCodeUrl: string = '';
  public subscriptionUrl: string = '';
  public appDomains: string[] = [];
  public apiDomains: string[] = [];

  constructor(
    private menuService: MenuService,
    private serviceService: ServiceService,
    private apiManager: APIManager,
    private toastService: ToastService,
  ) {
    this.platformSubscription = this.menuService.currentPlatform$.subscribe(menu => {
      this.currentPlatform = menu;
      this.currentPlatformClientsConfig = AllPlatformClientsConfig.filter(config =>
        config.platform.includes(this.currentPlatform)
      );
      this.selectedClient = this.currentPlatformClientsConfig.length > 0
        ? this.currentPlatformClientsConfig[0]
        : AllPlatformClientsConfig[0];
      if (this.service?.subscriptionUrl) {
        this.subscriptionUrl = this.addAgentToUrl(this.service.subscriptionUrl);
      }
    });
    this.serviceSubscription = this.serviceService.currentService$.subscribe(
      service => {
        this.service = service;
        const path = this.extractPathFromUrl(service.subscriptionUrl);
        this.subscribePath = path;
        if (service.subscriptionUrl) {
          this.subscriptionUrl = this.addAgentToUrl(service.subscriptionUrl);
        }
      },
      error => {
        console.error('Error fetching service:', error);
      }
    );

  }

  ngOnInit(): void {
    this.getAppDomains();
  }

  getAppDomains() {
    this.apiManager.getDeliveryDomains('This value will be overwritten by the environment variable').subscribe(domains => {
      if (domains.app.length === 0) {
        const currentDomain = window.location.hostname;
        this.appDomains = [currentDomain];
      } else {
        this.appDomains = domains.app;
      }
      if (domains.api.length === 0) {
        const currentDomain = window.location.hostname;
        this.appDomains = [currentDomain];
      } else {
        this.apiDomains = domains.api;
      }
    });
  }

  generateGuideUrl(ip: string, guidePath: string) {
    if (guidePath.startsWith('https://')) {
      return guidePath;
    }
    return `http://${ip}:9993/${guidePath}`;
  }

  openThirdPartyClientWebsite(url: string) {
    if (!url) {
      return;
    }
    window.open(url, '_blank');
  }


  onDownloadClick(clientName: string, domainIndex: number) {
    if (domainIndex > this.appDomains.length) {
      domainIndex = this.appDomains.length;
    }
    let downloadPath = '';
    switch (clientName) {
      case 'Clash X32':
        downloadPath = '/apps/clash-verge-windows-x86-setup.exe';
        break;
      case 'Clash X64':
        downloadPath = '/apps/clash-verge-windows-x64-setup.exe';
        break;
      case 'ClashMeta For Android':
        downloadPath = '/apps/ClashMetaForAndroid.apk';
        break;
      case 'Clash For Apple Silicon':
        downloadPath = '/apps/clash-verge-macos-aarch64.dmg';
        break;
      case 'Clash For Intel Chip':
        downloadPath = '/apps/clash-verge-macos-x64.dmg';
        break;
      default: '/apps/ClashMetaForAndroid.apk';
    }

    const domain = this.appDomains[domainIndex];
    const port = 10086;
    const newUrl = `https://${domain}:${port}${downloadPath}`;
    window.open(newUrl, '_blank');
  }

  getNumberedCircle(index: number): string {
    const baseCodePoint = 9311;
    return String.fromCodePoint(baseCodePoint + index);
  }

  copyUrl(path: string, domain: string) {
    let url = `https://${domain}${path}`;
    url = this.addAgentToUrl(url);
    navigator.clipboard.writeText(url).then(() => {
      this.toastService.success('已複製到剪貼簿');
    });
  }

  async showQrCode(path: string, domain: string) {
    let url = `https://${domain}${path}`;
    url = this.addAgentToUrl(url);
    this.subscriptionUrl = url;

    // 尽量不要使用三方的二维码服务，防止信息泄漏
    // this.qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(url)}`;
    this.qrCodeUrl = await QRCode.toDataURL(url);
    this.modalVisible = true;
  }

  extractPathFromUrl(url: string): string {
    const urlObject = new URL(url);
    return urlObject.pathname + urlObject.search + urlObject.hash || '';
  }

  onIconClick(clientName: string) {
    const selectedClient = AllPlatformClientsConfig.find(config => config.name === clientName);
    if (selectedClient) {
      this.selectedClient = selectedClient;
      if (this.service?.subscriptionUrl) {
        this.subscriptionUrl = this.addAgentToUrl(this.service.subscriptionUrl);
      }
    }
  }

  addAgentToUrl(url: string): string {
    const agent = this.appToAgent(this.selectedClient.name);
    url += `?agent=${agent}`;
    return url;
  }

  appToAgent(appName: string): string {
    switch (appName) {
      case 'Stash':
        return 'clash-fallback';
      case 'Shadowrocket':
        return 'shadowrocket';
      case 'shadowrocket':
        return 'shadowrocket';
      case 'Quantumult X':
        return 'quantumultx';
      case 'Clash X':
        return 'clash-fallback'
      default:
        return 'clash-hysteria';
    }
  }

  onConfirm() {
    this.modalVisible = false;
  }

  onCancel() {
    this.modalVisible = false;
  }

  onOpenAppClick() {
    if (!this.selectedClient.urlScheme) {
      this.toastService.error('該應用不支持直接打開');
      return;
    }

    const url = this.addAgentToUrl(this.subscriptionUrls[0]);
    const encodedUrl = encodeURIComponent(url);
    const appUrl = `${this.selectedClient.urlScheme}configure?url=${encodedUrl}`;

    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    document.body.appendChild(iframe);

    setTimeout(() => {
      const doc = document as any;
      if (doc.hidden || doc.webkitHidden) {
        document.body.removeChild(iframe);
      } else {
        document.body.removeChild(iframe);
        this.toastService.error('未檢測到應用，請先安裝');
      }
    }, 2000);

    iframe.src = appUrl;
  }
}